import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { usePageTitle } from '../../context/PageTitleContext';
import { logoutUserApi } from '../../services/authService';
import './TopNavbar.css';

const TopNavbar = () => {
  const { user, logout } = useAuth();
  const { isMobile, toggleSidebar, isOpen } = useSidebar();
  const { pageHeader } = usePageTitle() || {};
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await logoutUserApi(user?.username);
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout API error:', error);
      logout();
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="topnav">
      <div className="topnav-left">
        {/* Menu Toggle Button */}
        <button
          className={`menu-toggle-btn ${isOpen ? 'active' : ''}`}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>

        {pageHeader?.title && (
          <div className="topnav-page-title">
            {pageHeader.icon && (
              <span className="topnav-page-title-icon material-symbols-outlined">{pageHeader.icon}</span>
            )}
            <div className="topnav-page-title-text">
              <h1>
                {pageHeader.title}
                {pageHeader.badge && (
                  <span className="topnav-page-title-badge">{pageHeader.badge}</span>
                )}
              </h1>
              {pageHeader.subtitle && (
                <p className="topnav-page-subtitle">{pageHeader.subtitle}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="topnav-right">
        <button className="topnav-btn-icon" aria-label="Notifications">
          <span className="material-symbols-outlined">notifications</span>
          <span className="topnav-notification-badge" />
        </button>

        <button className="topnav-btn-icon" aria-label="Help">
          <span className="material-symbols-outlined">help_outline</span>
        </button>

        {/* Profile Menu */}
        <div className="profile-menu" ref={dropdownRef}>
          <button
            className="profile-button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-label="Profile menu"
          >
            <div className="profile-avatar">
              {getInitials(user?.name)}
            </div>
          </button>

          {dropdownOpen && (
            <div className="profile-dropdown">
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {getInitials(user?.name)}
                </div>
                <div className="dropdown-info">
                  <span className="dropdown-name">{user?.name || 'User'}</span>
                  <span className="dropdown-email">{user?.email || ''}</span>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <button
                className="dropdown-item"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                <span className="material-symbols-outlined">logout</span>
                {isLoggingOut ? 'Signing out...' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopNavbar;
